import { useMemo } from 'react';

import { useTypedSelector } from './useTypedSelector';

/**
 * Интерфейс эффективных настроек блока дашборда.
 * Учитывает логику "По умолчанию" (blocks) для атрибуции, типа регистрации и детализации.
 */
export interface BlockEffectiveSettings {
    /** Атрибуция: глобальная или из блока (если глобальная = 'blocks') */
    attribution: string[];
    /** Тип регистрации: глобальный или из блока (если глобальный = 'blocks') */
    type_applications: string[];
    /** Детализация: глобальная или из блока (если глобальная = 'blocks') */
    details: string;
    /** Глобальные фильтры дашборда */
    globalFilters: any[];
    /** Фильтры конкретного блока */
    blockFilters: any[];
    /** Объединённые фильтры (глобальные + блочные) для API запросов */
    mergedFilters: any[];
    /** Флаг: используется ли атрибуция из блока */
    isBlockAttribution: boolean;
    /** Флаг: используется ли тип регистрации из блока */
    isBlockTypeApplications: boolean;
    /** Флаг: используется ли детализация из блока */
    isBlockDetails: boolean;
}

const DEFAULT_ATTRIBUTION = ['1'];
const DEFAULT_TYPE_APPLICATIONS = ['1'];
const DEFAULT_DETAILS = 'd';

/**
 * Хук для получения эффективных настроек блока дашборда.
 * 
 * Логика работы:
 * 1. Если глобальная атрибуция = 'blocks' → берём атрибуцию из настроек блока
 * 2. Если глобальный тип регистрации = 'blocks' → берём тип из настроек блока
 * 3. Если глобальная детализация = 'blocks' → берём детализацию из настроек блока
 * 4. Фильтры всегда мержатся: глобальные + блочные (условие AND)
 * 
 * @param blockId - ID блока дашборда
 * @param options - Дополнительные опции
 * @returns Эффективные настройки блока
 */
export const useDashboardBlockSettings = (
    blockId: string | null | undefined,
    options: {
        /** Пропустить получение настроек (для модальных окон) */
        skip?: boolean;
        /** Использовать настройки из модального окна */
        isModal?: boolean;
    } = {}
): BlockEffectiveSettings => {
    const { skip = false, isModal = false } = options;

    // Глобальные настройки атрибуции дашборда
    const { attributesDashboard, attributesDashboardBlocks, attributes } = useTypedSelector(
        (state) => state.attributes
    );

    // Глобальные настройки детализации дашборда
    const { detailsDashboard, detailsDashboardBlocks, details: modalDetails } = useTypedSelector(
        (state) => state.details
    );

    // Фильтры
    const { filters: globalFilters, blocks: filterBlocks, dashboardModal } = useTypedSelector(
        (state) => state.filter
    );

    return useMemo(() => {
        // Для модального окна используем другие источники
        if (isModal) {
            return {
                attribution: normalizeToArray(attributes?.attribution) || DEFAULT_ATTRIBUTION,
                type_applications: normalizeToArray(attributes?.type_applications) || DEFAULT_TYPE_APPLICATIONS,
                details: modalDetails || DEFAULT_DETAILS,
                globalFilters: [],
                blockFilters: dashboardModal?.filters || [],
                mergedFilters: dashboardModal?.filters || [],
                isBlockAttribution: false,
                isBlockTypeApplications: false,
                isBlockDetails: false,
            };
        }

        // Если skip или нет blockId — возвращаем дефолтные значения
        if (skip || !blockId) {
            return {
                attribution: DEFAULT_ATTRIBUTION,
                type_applications: DEFAULT_TYPE_APPLICATIONS,
                details: DEFAULT_DETAILS,
                globalFilters: globalFilters || [],
                blockFilters: [],
                mergedFilters: globalFilters || [],
                isBlockAttribution: false,
                isBlockTypeApplications: false,
                isBlockDetails: false,
            };
        }

        // Проверяем, используется ли режим "По умолчанию" (blocks)
        const isBlockAttribution = attributesDashboard?.attribution?.[0] === 'blocks';
        const isBlockTypeApplications = attributesDashboard?.type_applications?.[0] === 'blocks';
        const isBlockDetails = detailsDashboard === 'blocks';

        // Получаем настройки блока
        const blockSettings = attributesDashboardBlocks?.[blockId];
        const blockDetailsValue = detailsDashboardBlocks?.[blockId];
        const blockFilters = filterBlocks?.[blockId]?.filters || [];

        // Вычисляем эффективную атрибуцию
        const attribution = isBlockAttribution
            ? normalizeToArray(blockSettings?.attribution) || DEFAULT_ATTRIBUTION
            : normalizeToArray(attributesDashboard?.attribution) || DEFAULT_ATTRIBUTION;

        // Вычисляем эффективный тип регистрации
        const type_applications = isBlockTypeApplications
            ? normalizeToArray(blockSettings?.type_applications) || DEFAULT_TYPE_APPLICATIONS
            : normalizeToArray(attributesDashboard?.type_applications) || DEFAULT_TYPE_APPLICATIONS;

        // Вычисляем эффективную детализацию
        const details = isBlockDetails
            ? blockDetailsValue || DEFAULT_DETAILS
            : detailsDashboard || DEFAULT_DETAILS;

        // Мержим фильтры: глобальные + блочные (AND)
        const mergedFilters = [
            ...(globalFilters || []),
            ...blockFilters,
        ];

        return {
            attribution,
            type_applications,
            details,
            globalFilters: globalFilters || [],
            blockFilters,
            mergedFilters,
            isBlockAttribution,
            isBlockTypeApplications,
            isBlockDetails,
        };
    }, [
        blockId,
        skip,
        isModal,
        attributesDashboard,
        attributesDashboardBlocks,
        attributes,
        detailsDashboard,
        detailsDashboardBlocks,
        modalDetails,
        globalFilters,
        filterBlocks,
        dashboardModal,
    ]);
};

/**
 * Нормализует значение к массиву строк.
 * Обрабатывает случаи когда значение может быть строкой или массивом.
 */
function normalizeToArray(value: string | string[] | undefined | null): string[] | null {
    if (!value) return null;
    if (Array.isArray(value)) return value;
    return [value];
}

export default useDashboardBlockSettings;
